'use client';

import { MaterialReactTable } from 'material-react-table';
import { Download } from '@mui/icons-material';
import { Box, Button, IconButton, ListItemIcon, Menu, MenuItem, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';
import GenericFormModal from '../form/GenericFormModal';
import { GENERIC_FORM_FIELD_TYPE } from '../form/GenericForm';
import { useQueryTrkEvents, useUpdateTrkEvent } from '@/hooks/api/useTrks';
import { useNotification } from '@/contexts/NotificationContext';
import { formatDate } from '@/stack-shared/utils/gen-utils';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const TrkEventPage: React.FC = () => {
  const [data, setData] = useState([] as any[]);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);

  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [sorting, setSorting] = useState([] as any[]);
  const [search, setSearch] = useState('');
  const [tableRefreshKey, setTableRefreshKey] = useState(0);

  const now = new Date();
  const lastMonth = new Date();
  lastMonth.setMonth(lastMonth.getMonth() - 1);

  const [startDate, setStartDate] = useState<Date | null>(lastMonth);
  const [endDate, setEndDate] = useState<Date | null>(now);

  const queryTrkEventsMutation = useQueryTrkEvents();
  const updateTrkEventMutation = useUpdateTrkEvent();
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();

  const columns = [
    { accessorKey: 'id', header: 'ID' },
    { accessorKey: 'date', header: 'Date', 
      Cell: ({ row }: any) => {
        return formatDate(row?.original?.date);
      }
    },
    { accessorKey: 'time', header: 'Time' },
    { accessorKey: 'locationName', header: 'Location' },
    { accessorKey: 'status', header: 'Status' },
    { accessorKey: 'event', header: 'Event' },
    { accessorKey: 'cassette1Load', header: 'Cassette 1 Load' },
    { accessorKey: 'cassette2Load', header: 'Cassette 2 Load' },
    { accessorKey: 'cassette3Load', header: 'Cassette 3 Load' },
    { accessorKey: 'cassette4Load', header: 'Cassette 4 Load' },
    { accessorKey: 'totalCassetteLoad', header: 'Total Cassette Load' },
    { accessorKey: 'balanceAtDeparture', header: 'Balance at Departure' },
    { accessorKey: 'resolveDate', header: 'Resolve Date',
      Cell: ({ row }: any) => {
        return formatDate(row?.original?.resolveDate);
     },
    },
    { accessorKey: 'initials', header: 'Technician', enableSorting: false},
    {
      id: 'actions',
      header: 'Actions',
      Cell: ({ row }: any) => {
        const rowData = row.original;
        const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
        const actionsOpen = Boolean(actionsAnchorEl);

        const handleMenuOpen = (event: any) => {
          setActionsAnchorEl(event.currentTarget);
        };

        const handleMenuClose = () => {
          setActionsAnchorEl(null);
        };

        return (
          <>
            <IconButton onClick={handleMenuOpen} color="primary" title="Options">
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={actionsAnchorEl}
              open={actionsOpen}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem onClick={() => {
                setSelectedEvent(rowData);
                setEditModalVisible(true);
              }}>
                <ListItemIcon><EditIcon /></ListItemIcon>
                <Typography>Edit</Typography>
              </MenuItem>
            </Menu>
          </>
        );
      },
    },
  ];

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'TRK Events' },
  ];

  const rightContent = (
    <>
      <Button variant="outlined" startIcon={<Download />} sx={{ textTransform: 'none' }}>
        Export
      </Button>
      {/* <Button onClick={() => setCreateModalVisible(true)} variant="contained" sx={{ textTransform: 'none' }}>
        + Add Event
      </Button> */}
    </>
  );

  const sharedEventFields = [{
    groupName: 'main',
    fields: [
      { label: 'Date', name: 'date', type: GENERIC_FORM_FIELD_TYPE.DATE, required: true, columns: 6 },
      { label: 'Time', name: 'time', type: GENERIC_FORM_FIELD_TYPE.TIME, required: false, columns: 6 },
      { label: 'Location', name: 'location', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 12 },
      { label: 'Status', name: 'status', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 6 },
      { label: 'Event', name: 'event', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 6 },
      { label: 'Technician', name: 'technician', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6 },
    ],
  }];

  const editEventFields = cloneDeep(sharedEventFields);

  const createEventFields = cloneDeep(sharedEventFields);
  createEventFields[0].fields.push({ label: 'Balance at Departure', name: 'balanceAtDeparture', type: GENERIC_FORM_FIELD_TYPE.NUMBER, required: false, columns: 12 });

  const fetchData = async () => {

    if (!startDate || !endDate) return;

    queryTrkEventsMutation.mutate({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        skip: pagination.pageIndex * pagination.pageSize,
        take: pagination.pageSize,
        search,
        orderBy: sorting[0]?.id || 'id',
        orderDirection: sorting[0]?.desc ? 'DESC' : 'ASC',
    }, {
        onSuccess: (result: any) => {
            setData(result.data || []);
            setTotalRows(result.total || 0);
        },
        onError: (error: any) => {
            showError(error.message || 'Failed to fetch TRK events');
        },
    });
  };

  useEffect(() => {
    fetchData();
  }, [pagination, sorting, search, tableRefreshKey, startDate, endDate]);

  return (
    <>
      <GenericFormModal 
        visible={createModalVisible} 
        onDismiss={() => setCreateModalVisible(false)}
        onSubmit={async (values: any) => {
          // Dispatch create action
          setCreateModalVisible(false);
          setTableRefreshKey(tableRefreshKey + 1);
        }}
        initialValues={{}}
        fieldGroups={createEventFields}
        title={'Create TRK Event'} 
      />

      <GenericFormModal 
        visible={editModalVisible} 
        onDismiss={() => setEditModalVisible(false)}
        onSubmit={async (values: any) => {
          updateTrkEventMutation.mutate(values, {
            onSuccess: (data: any) => {
              setEditModalVisible(false);
              setTableRefreshKey(tableRefreshKey + 1);
              showSuccess("TRK event updated successfully");
            },
            onError: (error: any) => {
              showError(error.message || 'Failed to update TRK event');
            },
          });
        }}
        initialValues={{
          id: selectedEvent?.id,
          date: selectedEvent?.date,
          time: selectedEvent?.time,
          location: selectedEvent?.location,
          status: selectedEvent?.status,
          event: selectedEvent?.event,
          technician: selectedEvent?.technician,
        }}
        fieldGroups={editEventFields}
        title={'Edit TRK Event'} 
      />

      <TitleBar breadcrumbs={breadcrumbs} title="TRK Events" rightContent={rightContent} />

      <MaterialReactTable
        columns={columns}
        data={data}
        manualPagination
        manualSorting
        enableGlobalFilter={false}
        onPaginationChange={setPagination}
        onSortingChange={setSorting}
        rowCount={totalRows}
        state={{ isLoading, pagination, sorting }}
        initialState={{ density: 'compact' }}
        renderTopToolbarCustomActions={() => (
          <Box display="flex" gap={2}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box display="flex" gap={2}>
              {/* Start Date Picker */}
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => {
                  if (newValue && typeof newValue === 'object' && 'toDate' in newValue) {
                    setStartDate(newValue.toDate());
                  } else if (newValue instanceof Date) {
                    setStartDate(newValue);
                  } else {
                    setStartDate(null);
                  }
                }}
                slotProps={{ textField: { variant: 'outlined', size: 'small' } }}
              />

              {/* End Date Picker */}
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => {
                  if (newValue && typeof newValue === 'object' && 'toDate' in newValue) {
                    setEndDate(newValue.toDate());
                  } else if (newValue instanceof Date) {
                    setEndDate(newValue);
                  } else {
                    setEndDate(null);
                  }
                }}
                slotProps={{ textField: { variant: 'outlined', size: 'small' } }}
              />
            </Box>
          </LocalizationProvider>

          <TextField placeholder="Search" variant="outlined" size="small" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ width: '300px' }} />

        </Box>
        )}
      />
    </>
  );
};

export default TrkEventPage;
